import { BasePage } from '../base.page';
import { Storage } from '@ionic/storage';
import { finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { AuthService } from 'src/app/services/auth.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { WardrobeService } from 'src/app/services/wardrobe.service';
import { PaginatedResponse } from 'src/app/models/paginated-response.model';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import {
  ProductListingParamsModel,
  ProductModel,
  ProductTypeModel,
} from 'src/app/models/wardrobe.model';
import { UserModel } from 'src/app/models/user.model';
import { FavoriteService } from 'src/app/services/favorite.service';
import { ProductFilterModalComponent } from 'src/app/components/product-filter-modal/product-filter-modal.component';
import {
  ActionSheetController,
  IonInfiniteScroll,
  LoadingController,
  ModalController,
  Platform,
  ToastController,
} from '@ionic/angular';
import { SearchFilterModalComponent } from 'src/app/components/search-filter-modal/search-filter-modal.component';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';

@Component({
  selector: 'shc-search-result',
  templateUrl: './search-result.page.html',
  styleUrls: ['./search-result.page.scss'],
})
export class SearchResultPage extends BasePage implements OnInit {
  query: string;
  isFiltered: boolean;
  activeTab: 'items' | 'users' = 'items';

  onTabChange(value: string | number) {
    if (value === 'items' || value === 'users') {
      this.activeTab = value;
    }
  }
  products: PaginatedResponse<ProductModel>;
  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;
  params: ProductListingParamsModel = new ProductListingParamsModel();
  filterSavedState: any;
  recentSearches: Array<string>;

  types: Array<ProductTypeModel> = [];
  selectedTypeId: number | null = null;

  matchedUsers: Array<UserModel> = [];

  constructor(
    protected platform: Platform,
    protected userService: UserService,
    protected authService: AuthService,
    protected translate: TranslateService,
    private errorService: ErrorHandlerService,
    protected loadingCtrl: LoadingController,
    toastController: ToastController,
    private modalCtrl: ModalController,
    private wardrobeService: WardrobeService,
    private router: Router,
    private route: ActivatedRoute,
    private actionSheetController: ActionSheetController,
    private localStorage: Storage,
    private favoriteService: FavoriteService,
  ) {
    super(
      platform,
      userService,
      authService,
      translate,
      loadingCtrl,
      toastController,
    );
  }

  ngOnInit() {
    this.query = this.router.getCurrentNavigation().extras?.state?.query;
    console.log(this.query);

    /**
     * When we redirect back to products search-result page from product detail the query will be empty which should
     * be the last searched text
     */
    if (this.query === undefined) {
      this.localStorage.get('recent-searches').then((res) => {
        console.log(res);
        if (res) {
          this.recentSearches = JSON.parse(res);
          this.query = this.recentSearches[0];
          console.log(this.recentSearches);
        }
        this.searchUser(this.query);
      });
    } else {
      this.localStorage.get('recent-searches').then((res) => {
        console.log(res);
        if (res) {
          this.recentSearches = JSON.parse(res);
        }
      });
      this.searchUser(this.query);
    }

    this.loadTypes();

    this.params = new ProductListingParamsModel();
    this.localStorage.get('search-filters').then((res) => {
      if (res) {
        this.filterSavedState = res;
        this.setFiltersParam(res);
      } else {
        this.setFiltersParam(res);
      }
    });
  }

  ionViewWillEnter() {
    this.params = new ProductListingParamsModel();
    this.params.page = 1;
  }

  loadTypes() {
    this.wardrobeService.typesList({ page: 1, per_page: 20 }).subscribe(
      (res: any) => {
        this.types = res?.data || [];
      },
      () => {
        this.types = [];
      },
    );
  }

  selectType(type: ProductTypeModel) {
    if (this.selectedTypeId === type.id) {
      this.selectedTypeId = null;
      this.params.type_id = undefined;
    } else {
      this.selectedTypeId = type.id;
      this.params.type_id = type.id;
    }
    this.params.page = 1;
    this.getProducts('');
  }

  searchUser(query: string) {
    if (!query) {
      this.matchedUsers = [];
      return;
    }

    this.userService.getUsers({ search: query, page: 1, per_page: 5 }).subscribe(
      (res: any) => {
        this.matchedUsers = (res?.data || []).sort(
          (a: UserModel, b: UserModel) => (b?.products_count || 0) - (a?.products_count || 0),
        );
      },
      () => {
        this.matchedUsers = [];
      },
    );
  }

  applyFavorites() {
    if (!this.products?.data?.length) {
      return;
    }
    this.favoriteService.favoritesList().subscribe(
      (res) => {
        const favoritedIds = new Set(res.data.map((p) => p.id));
        this.products.data.forEach((product) => {
          product.favorited = favoritedIds.has(product.id);
        });
      },
      () => {},
    );
  }

  toggleFavorite(product: ProductModel, event: Event) {
    event.stopPropagation();
    const previousValue = product.favorited;
    product.favorited = !previousValue;
    this.favoriteService.toggleFavorite(product.id).subscribe(
      (res) => {
        product.favorited = res.favorited;
      },
      (error) => {
        product.favorited = previousValue;
        this.errorService.handle(error);
      },
    );
  }

  getProducts(event) {
    this.showLoader();

    // check if filters are applying or not
    this.params = this.clean(this.params);
    this.isFiltered = this.isFilterApplied(this.params, 'search');

    this.wardrobeService
      .productsList(this.params)
      .pipe(finalize(() => this.hideLoader()))
      .subscribe(
        (res) => {
          if (this.products && this.products.meta) {
            if (this.params.page === 1) {
              this.products = res;
            } else {
              this.products.meta = res.meta;
              this.products.data = [...this.products.data, ...res.data];
            }
          } else {
            this.products = res;
          }
          this.authService.loggedIn.subscribe((loggedIn) => {
            if (loggedIn) {
              this.applyFavorites();
            }
          });
          if (event) {
            event.target.complete();
          }
          this.params.page++;
        },
        (error) => {
          this.errorService.handle(error);
        },
      );
  }

  search(value) {
    this.params.page = 1;
    this.params.search = this.query;

    if (
      !this.recentSearches.includes(this.query) ||
      this.recentSearches.lastIndexOf(this.query) !==
        this.recentSearches.length - 1
    ) {
      this.recentSearches.unshift(this.query);
    }
    this.localStorage.set(
      'recent-searches',
      JSON.stringify(this.recentSearches),
    );

    this.searchUser(this.query);
    this.getProducts('');
  }

  async sortBy() {
    const actionSheet = await this.actionSheetController.create({
      mode: 'md',
      header: this.translate.instant(marker('SortBy.Title')),
      cssClass: 'removeCancelButton',
      buttons: [
        {
          text: this.translate.instant(marker('SortBy.NewlyListed')),
          handler: () => {
            this.params.page = 1;
            this.params.sort = 'created_at';
            this.params.direction = 'DESC';
            this.getProducts('');
          },
        },
        {
          text: this.translate.instant(marker('SortBy.MostPopular')),
          handler: () => {
            this.params.page = 1;
            this.params.sort = 'views';
            this.params.direction = 'DESC';
            this.getProducts('');
          },
        },
      ],
    });
    await actionSheet.present();
  }

  async openFilters() {
    const modal = await this.modalCtrl.create({
      component: SearchFilterModalComponent,
      componentProps: {
        type: 'search',
        data: this.filterSavedState,
      },
      cssClass: 'product-filter',
      canDismiss: true,
      id: 'searchFilters',
    });
    modal.onDidDismiss().then((data) => {
      if (data && data.data) {
        if (data && data.data) {
          this.filterSavedState = data.data;
          this.setFiltersParam(data.data);
        }
      }
      this.modalCtrl.dismiss();
    });
    return await modal.present();
  }

  setFiltersParam(filters) {
    console.log(filters);
    this.params.page = 1;
    this.params.search = this.query;
    this.params.size_id = filters?.size?.id;
    this.params.type_id = filters?.type?.id;
    this.params.color_id = filters?.color?.id;
    this.params.brand_id = filters?.brand?.id;
    this.params.price_to = filters?.price?.upper;
    this.params.condition_id = filters?.condition;
    this.params.price_from = filters?.price?.lower;
    this.params.category_id = filters?.category?.id;
    this.params.latitude = filters?.latitude;
    this.params.longitude = filters?.longitude;
    this.params.location = filters?.location;
    this.getProducts('');
  }

  loadData(event): void {
    if (this.products.meta.current_page < this.products.meta.last_page) {
      this.getProducts(event);
    } else {
      this.infiniteScroll.disabled = true;
    }
  }
}
